/* eslint-disable no-param-reassign */
import { get, isUndefined, isNumber, isNull } from 'lodash';
import { METRIC_NAMES, RESOURCES_CONFIGURATION_INTERVAL as INTERVAL } from '@config/Consts';
import { convertTimeFrameToMinutes, getApiTimeParams, makeArrayOfStrings } from '@core/utils/utils';
import MsgConversationMetricsModel from '@core/models/MsgConversationMetricsModel';
import CSATModel from '@core/models/CSATModel';
import AgentStatusMetricsModel from '@core/models/AgentStatusMetricsModel';
import ManagerMetricsModel from '@core/models/ManagerMetricsModel';
import BaseClient from '@core/common/BaseClient';
import { RESOURCES as REPORTING_RESOURCES } from '@core/clients/ReportingClient';
import { AGENT_STATUS_RESOURCES } from '@core/clients/MsgHistoryClient';
import { RESOURCES as AGENT_MANAGER_RESOURCES, METRICS_RESOURCE_PARAMS } from '@core/clients/AgentManagerClient';
import QueueHealthModel from '@core/models/QueueHealthModel';

function prepareShiftStatusRequest(settings, httpMethod, params) {
  return {
    method: httpMethod,
    interval: INTERVAL.GENERAL_RESOURCE,
    body: params,
  };
}

function transformMessagingConversationRequest(settings) {
  const params = {
    // As in this widget we don't need the data aggregated by agent but just the total,
    // we need to add this param
    breakdown: false,
  };
  const httpMethod = BaseClient.HTTP_METHODS.POST;

  if (settings.timeframe) {
    params.timeframe = convertTimeFrameToMinutes(settings.timeframe);
  }
  if (settings.skills && settings.skills.length > 0) {
    params.skillIds = settings.skills;
  }
  if (settings.agentIds && settings.agentIds.length > 0) {
    params.agentIds = settings.agentIds;
  }

  return prepareShiftStatusRequest(settings, httpMethod, params);
}

function transformMessagingCsatDistributionRequest(settings) {
  const params = {};

  if (settings.timeframe) {
    params.timeframe = convertTimeFrameToMinutes(settings.timeframe);
  }
  if (settings.skills && settings.skills.length > 0) {
    params.skillIds = settings.skills.toString();
  }
  if (settings.agentIds && settings.agentIds.length > 0) {
    params.agentIds = settings.agentIds;
  }
  return prepareShiftStatusRequest(settings, BaseClient.HTTP_METHODS.POST, params);
}

function transformMetricsRequest(settings) {
  const params = {
    metricsToRetrieveByTime: [],
    metricsToRetrieveCurrentValue: [],
    responseSections: [
      METRICS_RESOURCE_PARAMS.RESPONSE_SECTIONS.ALL,
    ],
  };
  if (settings.useNewManagerApi) {
    params.metricsToRetrieveByTime.push(
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME.CLOSED);
    params.metricsToRetrieveCurrentValue.push(
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.ASSIGNED);
    params.metricsToRetrieveCurrentValue.push(
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.HUMAN_ONLINE_LOAD);
    params.metricsToRetrieveCurrentValue.push(
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.HUMAN_AWAY_LOAD);
    params.metricsToRetrieveCurrentValue.push(
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_CURRENT_VALUE.HUMAN_AGENT_LOAD);
    params.metricsToRetrieveByTime.push(
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME.CLOSED_BY_AGENT);
    params.metricsToRetrieveByTime.push(
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME.CLOSED_BY_CONSUMER);
    params.metricsToRetrieveByTime.push(
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME.AUTO_CLOSED);
    params.metricsToRetrieveByTime.push(
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME.AVG_CONVERSATIONS_DURATION);
  }
  if (settings.fetchResponseMetrics) {
    params.metricsToRetrieveByTime.push(
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME
        .AVG_TIME_TO_FIRST_RESPONSE_FIRST_ASSIGNMENT,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME.AVG_TIME_TO_RESPONSE,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME.AVG_WAIT_TIME_FIRST_RESPONSE,
      METRICS_RESOURCE_PARAMS.REQUEST_METRICS_TO_RETRIEVE_BY_TIME.AVG_WAIT_TIME);
  }

  params.filters = {};
  if (settings.groupIds && settings.groupIds.length > 0) {
    params.filters.agentGroupIds = makeArrayOfStrings(settings.groupIds);
  }
  if (settings.timeframe) {
    const apiTimeParams = getApiTimeParams(settings.timeframe);
    params.filters.time = { from: apiTimeParams.lastUpdateFrom, to: apiTimeParams.to };
  }
  if (settings.skills && settings.skills.length > 0) {
    params.filters.skillIds = makeArrayOfStrings(settings.skills);
  }
  return prepareShiftStatusRequest(settings, BaseClient.HTTP_METHODS.POST, params);
}

function transformAgentStatusRequest(settings, params) {
  if (settings.status) {
    params.status = settings.status;
  }

  if (settings.groupIds && settings.groupIds.length > 0) {
    params.agentGroupIds = settings.groupIds;
  }
  if (settings.skills && settings.skills.length > 0) {
    params.skillIds = makeArrayOfStrings(settings.skills);
  }

  return prepareShiftStatusRequest(settings, BaseClient.HTTP_METHODS.POST, params);
}

function transformAgentStatusAssignedRequest(settings) {
  const params = {};

  if (settings.agentIds && settings.agentIds.length > 0) {
    params.agentIds = settings.agentIds.split(',');
  }

  return transformAgentStatusRequest(settings, params);
}

function transformAgentStatusLoadRequest(settings) {
  const params = {};

  if (settings.humanAgentIds && settings.humanAgentIds.length > 0) {
    params.agentIds = settings.humanAgentIds;
  }

  return transformAgentStatusRequest(settings, params);
}

function transformMessagingQueueHealthRequest(settings) {
  const params = {
    // As in this widget we don't need the data aggregated by agent but just the total,
    // we need to add this param
    breakdown: false,
    metrics: 'overdue,queue',
    groupIds: 'all',
  };

  if (settings.groupIds && settings.groupIds.length > 0) {
    params.groupIds = settings.groupIds.toString();
  }

  if (settings.skills && settings.skills.length > 0) {
    params.skillIds = settings.skills.toString();
  }

  return prepareShiftStatusRequest(settings, BaseClient.HTTP_METHODS.POST, params);
}

function transformMessagingConversationResponse(response) {
  let data = null;
  const conversationsRecord = get(response, 'shiftStatusResponse.data.metricsTotals') || get(response, 'shiftStatusResponse.data.agentsMetrics.metricsTotals');
  if (!isUndefined(conversationsRecord)) {
    data = new MsgConversationMetricsModel({
      [METRIC_NAMES.CLOSED_CONVERSATIONS]: conversationsRecord.totalResolvedConversations,
      // Remove response time
      // [METRIC_NAMES.RESPONSE_TIME]: conversationsRecord.avgTime_resolvedConversations,
    });
  }

  return data;
}

function transformMessagingCsatDistributionResponse(response) {
  let data = null;
  // AE-7957: if filtered by skills the csat score is located into the skillsMetricsPerAgent field
  // otherwise it is taken from the agentsMetrics field

  // csatRecordFilterBySkills is the csat score returned by the api if filter by skills is applied
  const csatRecordFilterBySkills =
    get(response, 'shiftStatusResponse.data.skillsMetricsPerAgent.metricsTotals.csat_score');

  // csatRecordGlobal is the csat score returned by the api no matter which filter is applied (even
  // if filter by skill is applied.
  const csatAgentsMetrics = get(response, 'shiftStatusResponse.data.agentsMetrics.metricsTotals.csat_score');
  const csatRecordGlobal = get(response, 'shiftStatusResponse.data.metricsTotals.csat_score', csatAgentsMetrics);

  // if filter by skill is applied, the csat score returned by csatRecordGlobal is not relevant. the
  // relevant score is supplied by csatRecordFilterBySkills. For all the other cases, the score is
  // supplied by csatRecordGlobal
  const csatRecord = !isUndefined(csatRecordFilterBySkills) ?
    csatRecordFilterBySkills : csatRecordGlobal;

  if (!isUndefined(csatRecord) && !isNull(csatRecord)) {
    data = new CSATModel({
      [METRIC_NAMES.CSAT]: isNumber(csatRecord) ? Math.round(csatRecord * 100) : null,
    });
  }

  return data;
}

function convertToPercentage(score) {
  return isNumber(score) ? Math.round(score * 100) : undefined;
}

function transformMetricsResponse(response) {
  let result = null;
  const metricsData = {};
  const metrics = get(response, 'shiftStatusResponse.data.all');
  if (metrics) {
    // closed and assigned conversations are not time based (like the three othere metrics below)
    // meaning that if the api returns empty it means that there was no event of type
    // 'MessagePublishEvent' that has been counted therefore we can set the value to zero
    // for these metrics if no events found.
    metricsData[METRIC_NAMES.CLOSED_CONVERSATIONS] =
        metrics.closedConversations || 0;

    metricsData[METRIC_NAMES.ASSIGNED_CONVERSATIONS] =
        metrics.assignedConversations || 0;

    metricsData[METRIC_NAMES.AVG_WAIT_TIME] =
        metrics[METRIC_NAMES.AVG_WAIT_TIME];

    metricsData[METRIC_NAMES.AVG_WAIT_TIME_FIRST_RESPONSE] =
        metrics[METRIC_NAMES.AVG_WAIT_TIME_FIRST_RESPONSE];

    metricsData[METRIC_NAMES.AVG_TIME_TO_RESPONSE] =
        metrics[METRIC_NAMES.AVG_TIME_TO_RESPONSE];

    metricsData[METRIC_NAMES.AVG_TIME_TO_FIRST_RESPONSE_FIRST_ASSIGNMENT] =
        metrics[METRIC_NAMES.AVG_TIME_TO_FIRST_RESPONSE_FIRST_ASSIGNMENT];

    metricsData[METRIC_NAMES.HUMAN_ONLINE_LOAD] =
      convertToPercentage(metrics[METRIC_NAMES.HUMAN_ONLINE_LOAD]);

    metricsData[METRIC_NAMES.HUMAN_AWAY_LOAD] =
      convertToPercentage(metrics[METRIC_NAMES.HUMAN_AWAY_LOAD]);

    metricsData[METRIC_NAMES.LOAD_CONVERSATIONS] =
      convertToPercentage(metrics[METRIC_NAMES.HUMAN_AGENT_LOAD]);

    metricsData[METRIC_NAMES.CLOSED_BY_CONSUMER] =
      metrics[METRIC_NAMES.CLOSED_BY_CONSUMER] || 0;

    metricsData[METRIC_NAMES.CLOSED_BY_AGENT] =
      metrics[METRIC_NAMES.CLOSED_BY_AGENT] || 0;

    metricsData[METRIC_NAMES.AUTO_CLOSED] =
      metrics[METRIC_NAMES.AUTO_CLOSED] || 0;

    metricsData[METRIC_NAMES.AVG_CONVERSATIONS_DURATION] =
      metrics[METRIC_NAMES.AVG_CONVERSATIONS_DURATION];

    result = new ManagerMetricsModel(metricsData);
  }
  return result;
}

function transformAgentStatusAssignedResponse(response) {
  let data = null;
  const agentStatusRecord = get(response, 'shiftStatusResponse.data.summaryResults');
  if (!isUndefined(agentStatusRecord)) {
    data = new AgentStatusMetricsModel({
      [METRIC_NAMES.ASSIGNED_CONVERSATIONS]: agentStatusRecord.openAssignedConversations,
    });
  }
  return data;
}

function transformAgentStatusLoadResponse(response) {
  let data = null;
  const agentStatusRecord = get(response, 'shiftStatusResponse.data.summaryResults');
  if (!isUndefined(agentStatusRecord)) {
    data = new AgentStatusMetricsModel({
      [METRIC_NAMES.LOAD_CONVERSATIONS]: agentStatusRecord.weightedAvgLoad,
    });
  }
  return data;
}

function transformMessagingQueueHealthResponse(response) {
  let data = null;
  const queueHealthTotalRecord = get(response, 'shiftStatusResponse.data.metricsTotal');
  if (!isUndefined(queueHealthTotalRecord)) {
    data = new QueueHealthModel({
      [METRIC_NAMES.OVERDUE_ASSIGNED]: queueHealthTotalRecord[METRIC_NAMES.OVERDUE_ASSIGNED],
      [METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS]:
        queueHealthTotalRecord[METRIC_NAMES.SKILL_UNASSIGNED_CONVERSATIONS],
      [METRIC_NAMES.OVERDUE_IN_QUEUE]: queueHealthTotalRecord[METRIC_NAMES.OVERDUE_IN_QUEUE],
    });
  }
  return data;
}

const RESOURCE_TRANSFORM_REQUEST_METHOD_MAP = {
  [REPORTING_RESOURCES.MESSAGING_CONVERSATIONS]: transformMessagingConversationRequest,
  [REPORTING_RESOURCES.MESSAGING_CSAT_DISTRIBUTIONS]: transformMessagingCsatDistributionRequest,
  [AGENT_MANAGER_RESOURCES.METRICS]: transformMetricsRequest,
  [AGENT_STATUS_RESOURCES.AGENT_STATUS_LOAD]: transformAgentStatusLoadRequest,
  [AGENT_STATUS_RESOURCES.AGENT_STATUS_ASSIGNED]: transformAgentStatusAssignedRequest,
  [REPORTING_RESOURCES.MESSAGING_CURRENT_QUEUE_HEALTH]: transformMessagingQueueHealthRequest,
};

const RESOURCE_TRANSFORM_RESPONSE_METHOD_MAP = {
  [REPORTING_RESOURCES.MESSAGING_CONVERSATIONS]: transformMessagingConversationResponse,
  [REPORTING_RESOURCES.MESSAGING_CSAT_DISTRIBUTIONS]: transformMessagingCsatDistributionResponse,
  [AGENT_MANAGER_RESOURCES.METRICS]: transformMetricsResponse,
  [AGENT_STATUS_RESOURCES.AGENT_STATUS_LOAD]: transformAgentStatusLoadResponse,
  [AGENT_STATUS_RESOURCES.AGENT_STATUS_ASSIGNED]: transformAgentStatusAssignedResponse,
  [REPORTING_RESOURCES.MESSAGING_CURRENT_QUEUE_HEALTH]: transformMessagingQueueHealthResponse,
};

function transformRequest(resourceName, settings) {
  return RESOURCE_TRANSFORM_REQUEST_METHOD_MAP[resourceName](settings);
}

function transformResponse(resourceName, response) {
  return RESOURCE_TRANSFORM_RESPONSE_METHOD_MAP[resourceName](response);
}

export default {
  transformRequest,
  transformResponse,
};
